import { useState, useEffect, useMemo } from 'react';
import { getLeadExecutions } from '../services/leadExecutionService';
import toast from 'react-hot-toast';

export const useLeadExecutions = () => {
  const [allExecutions, setAllExecutions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Pagination & Filtering state
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Lead Statuses');
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'completed'
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [datePreset, setDatePreset] = useState('all');
  const itemsPerPage = 10;

  const fetchExecutions = async () => {
    try {
      setIsLoading(true);
      const data = await getLeadExecutions();
      setAllExecutions(data.data || []);
    } catch (error) {
      console.error('Error fetching lead executions:', error);
      toast.error('Failed to load lead executions');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchExecutions();
  }, []);

  // Filter logic
  const filteredExecutions = useMemo(() => {
    return allExecutions.filter(ex => {
      let matches = true;
      
      const exStatusKey = (ex.leadId?.leadStatus || ex.status || ex.projectStatus || 'new').replace(/_/g, ' ').toLowerCase();

      // Active Tab Filter
      if (activeTab === 'completed') {
        if (exStatusKey !== 'completed') matches = false;
      } else if (activeTab === 'pending') {
        if (exStatusKey === 'completed') matches = false;
      }

      if (statusFilter !== 'All Lead Statuses' && statusFilter !== 'all') {
        matches = matches && (exStatusKey === statusFilter.toLowerCase());
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const l = ex.leadId || {};
        const verveId = (l.verveId || '').toLowerCase();
        const customId = (l.customId || '').toLowerCase();
        const phone = (l.phoneNumber || l.phone || '').toLowerCase();
        const firstName = (l.firstName || '').toLowerCase();
        const lastName = (l.lastName || '').toLowerCase();
        const fullName = l.customerName ? l.customerName.toLowerCase() : `${firstName} ${lastName}`.trim();
        
        matches = matches && (
          verveId.includes(query) || 
          customId.includes(query) || 
          phone.includes(query) || 
          firstName.includes(query) || 
          lastName.includes(query) || 
          fullName.includes(query)
        );
      }
      
      if (dateRange.from || dateRange.to) {
        const rowDateStr = ex.createdAt || ex.startDate;
        if (rowDateStr) {
          const rowDate = new Date(rowDateStr);
          rowDate.setHours(0,0,0,0);
          
          if (dateRange.from) {
            const fromD = new Date(dateRange.from);
            fromD.setHours(0,0,0,0);
            if (rowDate < fromD) matches = false;
          }
          if (dateRange.to) {
            const toD = new Date(dateRange.to);
            toD.setHours(23,59,59,999);
            if (rowDate > toD) matches = false;
          }
        } else {
          matches = false;
        }
      }
      return matches;
    });
  }, [allExecutions, statusFilter, searchQuery, dateRange, activeTab]);

  // Pagination logic
  const totalItems = filteredExecutions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const paginatedExecutions = filteredExecutions.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  // Stats calculation
  const stats = useMemo(() => {
    const activeExecutions = filteredExecutions.filter(e => !e.leadId?.isDisabled);
    const totalExecutions = activeExecutions.length;
    const completedExecutions = activeExecutions.filter(e => {
      const statusKey = (e.leadId?.leadStatus || e.status || e.projectStatus || '').toLowerCase();
      return statusKey === 'completed';
    }).length;
    const totalRevenue = activeExecutions.reduce((acc, curr) => acc + (curr.projectValue || curr.leadId?.quotationAmount || curr.leadId?.budget || 0), 0);
    const totalBalance = activeExecutions.reduce((acc, curr) => acc + ((curr.projectValue || curr.leadId?.quotationAmount || curr.leadId?.budget || 0) - (curr.amountReceived || 0)), 0);

    return { totalExecutions, completedExecutions, totalRevenue, totalBalance };
  }, [filteredExecutions]);

  return {
    executions: paginatedExecutions, // Return paginated items directly to the table
    allExecutions,
    stats,
    isLoading,
    refetch: fetchExecutions,
    
    // UI states
    page,
    setPage,
    totalPages,
    totalItems,
    activeTab,
    setActiveTab,
    searchQuery,
    setSearchQuery: (query) => { setSearchQuery(query); setPage(1); },
    statusFilter,
    setStatusFilter: (status) => { setStatusFilter(status); setPage(1); },
    
    // Add additional mock functions to prevent errors when plugging in FilterSection
    customerName: '',
    setCustomerName: () => {},
    dateRange,
    datePreset,
    handleStartDateChange: (from) => { setDateRange(prev => ({ ...prev, from })); setPage(1); setDatePreset('custom'); },
    handleEndDateChange: (to) => { setDateRange(prev => ({ ...prev, to })); setPage(1); setDatePreset('custom'); },
    handleDatePreset: (preset) => {
      setDatePreset(preset);
      setPage(1);
      if (preset === 'all' || preset === 'custom') {
        if (preset === 'all') setDateRange({ from: '', to: '' });
        return;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let fromDate = new Date(today);
      
      if (preset === 'today') {
        // fromDate is already today
      } else if (preset === 'this_week') {
        fromDate.setDate(today.getDate() - today.getDay());
      } else if (preset === 'this_month') {
        fromDate.setDate(1);
      } else if (preset === 'this_year') {
        fromDate.setMonth(0, 1);
      }
      
      const tzOffset = (new Date()).getTimezoneOffset() * 60000;
      const format = (d) => new Date(d - tzOffset).toISOString().split('T')[0];
      
      setDateRange({ from: format(fromDate), to: '' });
    },
    customerList: [],
    statusList: [...new Set(allExecutions.map(e => {
        const s = e.leadId?.leadStatus || e.status || e.projectStatus || 'new';
        return s.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }).filter(Boolean))],
    handleExportProjects: () => {
      import('xlsx').then((XLSX) => {
        const getMemberName = (user) => {
          if (!user) return "—";
          if (typeof user === 'string') return user;
          return user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown';
        };
        const formatDateStr = (dateStr) => {
          if (!dateStr) return "—";
          return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        };
        
        const exportData = filteredExecutions.map(row => {
          const l = row.leadId || {};
          const fullName = l.customerName || `${l.firstName || ""} ${l.lastName || ""}`.trim() || "No Name";
          
          const addressParts = [
            l.addressLine1, l.addressLine2, l.city, l.district, l.state, l.pincode
          ].filter(Boolean);
          const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : "—";
          
          let lastNote = row.notes || l.lastActivityNote || l.notes || "No notes yet";
          if (lastNote && typeof lastNote === 'string' && lastNote.includes(' | Note: ')) {
            lastNote = lastNote.split(' | Note: ')[1];
          }
  
          const dateToUse = row.createdAt || row.startDate;
          
          const pVal = row.projectValue || l.quotationAmount || l.budget || 0;
          const rec = row.amountReceived || 0;
          const exp = (row.projectExpenses || 0) + (row.materialExpenses || 0);
          
          const statusKey = l.leadStatus || row.status || row.projectStatus || 'new';
  
          // Calculate progress
          const LEAD_EXECUTION_STAGES = [
            "initial_quotation", "budget", "order_placing", "measurements", "production",
            "material_delivery", "installation", "handover", "completed"
          ];
          let activeIdx = LEAD_EXECUTION_STAGES.findIndex(s => s.toLowerCase() === statusKey.toLowerCase());
          if (activeIdx === -1) activeIdx = 0;
          const progress = Math.max(0, Math.round(((activeIdx + 1) / LEAD_EXECUTION_STAGES.length) * 100));
  
          return {
            "Verve ID": l.customId || l.verveId || "—",
            "Customer Name": fullName,
            "Phone": l.phoneNumber || l.phone || "No Phone",
            "Email": l.email || "—",
            "Address": fullAddress,
            "Source": l.source === 'Bulk' ? 'Bulk Import' : (l.source || "Manual"),
            "Reference Type": l.referenceType || "—",
            "Staff Name": getMemberName(l.telecaller || l.createdBy),
            "Site Exec": getMemberName(l.siteExecutive),
            "Designer": getMemberName(l.designer),
            "Status": l.status === "drop" ? "drop" : (l.teleStatus || l.status),
            "Notes": lastNote,
            "Created Date": formatDateStr(row.createdAt),
            "App Schedule Date": l.appointmentDate ? new Date(l.appointmentDate).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "—",
            "Start date": formatDateStr(dateToUse),
            "Project value (₹)": pVal,
            "Project expenses (₹)": row.projectExpenses || 0,
            "Material Expenses (₹)": row.materialExpenses || 0,
            "Amount recieved (₹)": rec,
            "Pending balance (₹)": pVal - rec,
            "profit/loss (₹)": rec - exp,
            "Lead Status": statusKey.replace(/_/g, ' '),
            "Site Status": row.projectStatus || row.status || 'Not started',
            "Progress": `${progress}%`
          };
        });
  
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Lead Executions");
        
        XLSX.writeFile(workbook, `Lead_Executions_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success('Export downloaded successfully');
      }).catch(err => {
        console.error('Export Error:', err);
        toast.error('Failed to export data');
      });
    }
  };
};
